// L'installation guidée (lot 6.1) : reconnaître le téléphone et le navigateur pour dire LE geste qui installe Alfred,
// garder l'invitation à installer que Chrome et Edge proposent (`beforeinstallprompt`), et se souvenir qu'on a fini
// ou remis à plus tard. La détection est pure (testée) ; le reste lit le navigateur au moment de l'appel.

/**
 * Où l'on est, du point de vue de l'installation :
 *   · iphone-safari, iphone-chrome, iphone-firefox, iphone-autre : sur iPhone et iPad, l'app ajoutée à l'écran
 *     d'accueil est la seule qui reçoit des notifications (iOS 16.4 et plus) ; le geste dépend du navigateur ;
 *   · android-firefox, android (Chrome, Edge, Samsung Internet…) : installer est conseillé, les notifications
 *     marchent aussi dans le navigateur ;
 *   · ordinateur : rien à installer, les notifications marchent dans le navigateur.
 */
export type Plateforme = 'iphone-safari' | 'iphone-chrome' | 'iphone-firefox' | 'iphone-autre' | 'android' | 'android-firefox' | 'ordinateur';

export function plateformeDe(ua: string, pointsTactiles = 0): Plateforme {
  // iPadOS se présente comme un Mac ; le tactile le trahit.
  const ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && pointsTactiles > 1);
  if (ios) {
    if (/CriOS/.test(ua)) return 'iphone-chrome';
    if (/FxiOS/.test(ua)) return 'iphone-firefox';
    // Les autres navigateurs d'iOS ajoutent leur marque (EdgiOS, OPiOS…) ; Safari, aucune.
    if (/EdgiOS|OPiOS|OPT\/|YaBrowser|DuckDuckGo|GSA\//.test(ua)) return 'iphone-autre';
    return 'iphone-safari';
  }
  if (/Android/.test(ua)) return /Firefox/.test(ua) ? 'android-firefox' : 'android';
  return 'ordinateur';
}

export function estIphone(p: Plateforme): boolean {
  return p.startsWith('iphone');
}

/** Les gestes qui installent Alfred, dans l'ordre, pour cette plateforme. Vide : rien à installer. */
export function gestesInstallation(p: Plateforme): string[] {
  switch (p) {
    case 'iphone-safari':
      return [
        'En bas de Safari, touchez « ⋯ » puis « Partager » (ou directement le bouton Partager, le carré avec une flèche vers le haut).',
        'Faites défiler et touchez « Sur l’écran d’accueil ». Laissez « Ouvrir comme app web » activé.',
        'Touchez « Ajouter », en haut à droite.',
      ];
    case 'iphone-chrome':
      return [
        'Dans la barre d’adresse de Chrome, touchez le bouton Partager (le carré avec une flèche vers le haut).',
        'Touchez « Sur l’écran d’accueil », puis « Ajouter ».',
      ];
    case 'iphone-firefox':
      return [
        'En bas de Firefox, touchez le menu (☰), puis « Partager ».',
        'Touchez « Sur l’écran d’accueil », puis « Ajouter ».',
      ];
    case 'iphone-autre':
      return [
        'Ouvrez ce lien dans Safari : c’est le plus simple pour installer Alfred sur iPhone.',
        'Puis Partager → « Sur l’écran d’accueil » → « Ajouter ».',
      ];
    case 'android':
      return [
        'Touchez le menu du navigateur (⋮, en haut à droite).',
        'Touchez « Installer l’application » (ou « Ajouter à l’écran d’accueil »), puis « Installer ».',
      ];
    case 'android-firefox':
      return [
        'Touchez le menu de Firefox (⋮).',
        'Touchez « Ajouter à l’écran d’accueil », puis « Ajouter ».',
      ];
    case 'ordinateur':
      return [];
  }
}

// --- ce qui lit le navigateur ----------------------------------------------------------------------------------------

/** Ce que Chrome, Edge et Samsung Internet passent avec `beforeinstallprompt` (absent des types de TypeScript). */
interface InvitationInstallation extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let invitation: InvitationInstallation | null = null;
const abonnes = new Set<() => void>();

/**
 * À appeler au démarrage, avant le premier rendu : le navigateur ne propose l'installation qu'une fois, très tôt. On
 * la garde pour le bouton « Installer » de l'écran, au lieu du bandeau du navigateur.
 */
export function ecouterInstallation(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    invitation = e as InvitationInstallation;
    abonnes.forEach((f) => f());
  });
  window.addEventListener('appinstalled', () => {
    invitation = null;
    abonnes.forEach((f) => f());
  });
}

/** Le navigateur peut-il installer Alfred d'un bouton ? */
export function installableDirectement(): boolean {
  return invitation !== null;
}

/** Être prévenu quand l'invitation arrive ou disparaît (l'écran affiche alors le bouton, ou plus). */
export function surInvitation(f: () => void): () => void {
  abonnes.add(f);
  return () => abonnes.delete(f);
}

/** Ouvre la fenêtre d'installation du navigateur. Vrai si la personne a accepté. */
export async function installerDirectement(): Promise<boolean> {
  if (!invitation) return false;
  const i = invitation;
  await i.prompt();
  const { outcome } = await i.userChoice;
  invitation = null;
  return outcome === 'accepted';
}

export function plateformeCourante(): Plateforme {
  return plateformeDe(navigator.userAgent, navigator.maxTouchPoints);
}

// --- fini, ou remis à plus tard ---------------------------------------------------------------------------------------

const CLE = 'alfred.installation';
/** « Plus tard » : le bandeau du brief revient au bout d'une semaine. */
const ATTENTE_MS = 7 * 24 * 3600 * 1000;

/** L'état gardé sur cet appareil : « fait », ou la date du dernier « plus tard ». */
export function lireSuivi(): { fait: boolean; plusTardLe: number | null } {
  try {
    const v = localStorage.getItem(CLE);
    if (v === 'fait') return { fait: true, plusTardLe: null };
    const n = v ? Number(v) : NaN;
    return { fait: false, plusTardLe: Number.isFinite(n) ? n : null };
  } catch {
    return { fait: false, plusTardLe: null };
  }
}

export function noterFait(): void {
  try {
    localStorage.setItem(CLE, 'fait');
  } catch {
    /* l'écran reviendra : sans conséquence */
  }
}

export function noterPlusTard(maintenant = Date.now()): void {
  try {
    localStorage.setItem(CLE, String(maintenant));
  } catch {
    /* idem */
  }
}

/** Le bandeau du brief doit-il inviter à installer ? (Pas si c'est fait, ni dans la semaine d'un « plus tard ».) */
export function bandeauAttendu(suivi: { fait: boolean; plusTardLe: number | null }, maintenant = Date.now()): boolean {
  if (suivi.fait) return false;
  return suivi.plusTardLe === null || maintenant - suivi.plusTardLe >= ATTENTE_MS;
}
